/**
 * NotificationBadge Component
 *
 * Bell icon with unread count badge.
 * Opens the NotificationCenter when clicked.
 */

import { forwardRef } from 'react';
import { Bell } from 'lucide-react';
import { Button, type ButtonProps } from '@/components/ui/button';
import { useNotificationContext } from '@/context/NotificationContext';
import { cn } from '@/lib/core/utils';

export const NotificationBadge = forwardRef<HTMLButtonElement, ButtonProps>(
  function NotificationBadge({ className, ...props }, ref) {
    const { unreadCount, countByPriority, systemNotificationCount } = useNotificationContext();

    const hasUrgent = countByPriority.urgent > 0;
    const hasActionRequired = countByPriority.action_required > 0;
    const totalCount = unreadCount + systemNotificationCount;

    // Determine badge color based on highest priority
    const getBadgeColor = () => {
      if (hasUrgent) return 'bg-red-500';
      if (hasActionRequired) return 'bg-amber-500';
      return 'bg-primary';
    };

    return (
      <Button
        ref={ref}
        variant="outline"
        size="sm"
        {...props}
        type="button"
        className={cn(
          'relative overflow-visible',
          className,
        )}
        aria-label={`Notifications${totalCount > 0 ? ` (${totalCount} unread)` : ''}`}
      >
        <Bell className={cn(
          'h-4 w-4 transition-colors',
          hasUrgent && 'text-danger',
        )} />

        {/* Unread indicator dot */}
        {totalCount > 0 && (
          <span
            className={cn(
              'absolute top-0.5 right-0.5 block h-2 w-2 rounded-full ring-1 ring-background',
              getBadgeColor()
            )}
          />
        )}

        {/* Pulse animation for urgent */}
        {hasUrgent && (
          <span
            className={cn(
              'absolute top-0.5 right-0.5 block h-2 w-2 rounded-full',
              'animate-ping bg-red-500 opacity-75'
            )}
          />
        )}

        <span className="sr-only">View notifications</span>
      </Button>
    );
  }
);
